import type { DateFormat, TimeFormat } from '@treneo/shared/constants';
import {
  convertDateToIsoString,
  formatDateTimeLocaleWithFallback,
  formatDateTimeWithFormat
} from '@treneo/shared/utils';

import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid';

type TranslationFunction = (key: string) => string;

export type LastLoginColumnOptions<T extends GridValidRowModel> = Partial<GridColDef<T>> & {
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  fallbackLabel?: string;
};

export const createEmailColumn = <T extends { email: string }>(
  t: TranslationFunction,
  options?: Partial<GridColDef<T>>
): GridColDef<T> => ({
  field: 'email',
  headerName: t('admin.email'),
  width: 250,
  flex: 1,
  minWidth: 200,
  sortable: true,
  filterable: true,
  ...options
});

export const createDisplayNameColumn = <
  T extends { displayName?: string | null; firstName?: string; lastName?: string }
>(
  t: TranslationFunction,
  options?: Partial<GridColDef<T>>
): GridColDef<T> => ({
  field: 'displayName',
  headerName: t('admin.displayName'),
  width: 200,
  flex: 1,
  minWidth: 150,
  valueGetter: (_value, row) =>
    row.displayName ?? (`${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '-'),
  sortable: true,
  filterable: true,
  ...options
});

export const createFirstNameColumn = <T extends { firstName?: string }>(
  t: TranslationFunction,
  options?: Partial<GridColDef<T>>
): GridColDef<T> => ({
  field: 'firstName',
  headerName: t('admin.firstName'),
  width: 150,
  sortable: true,
  filterable: true,
  ...options
});

export const createLastNameColumn = <T extends { lastName?: string }>(
  t: TranslationFunction,
  options?: Partial<GridColDef<T>>
): GridColDef<T> => ({
  field: 'lastName',
  headerName: t('admin.lastName'),
  width: 150,
  sortable: true,
  filterable: true,
  ...options
});

export const createLastLoginColumn = <
  T extends GridValidRowModel & { lastLoginAt?: string | null | Date }
>(
  t: TranslationFunction,
  options?: LastLoginColumnOptions<T>
): GridColDef<T> => {
  const { dateFormat, timeFormat, fallbackLabel, ...gridOptions } = options ?? {};
  const neverLabel = fallbackLabel ?? t('admin.never');
  const useConfigFormat = dateFormat != null && timeFormat != null;

  return {
    field: 'lastLoginAt',
    headerName: t('admin.lastLogin'),
    width: 180,
    sortable: true,
    filterable: true,
    valueFormatter: (value: unknown) => {
      const dateValue = value as Date | string | null | undefined;
      if (useConfigFormat) {
        return formatDateTimeWithFormat(dateValue, dateFormat, timeFormat, neverLabel);
      }
      return formatDateTimeLocaleWithFallback(convertDateToIsoString(dateValue), neverLabel);
    },
    ...gridOptions
  };
};

export const createNameColumn = <T extends { name: string }>(
  t: TranslationFunction,
  options?: Partial<GridColDef<T>>
): GridColDef<T> => ({
  field: 'name',
  headerName: t('admin.name'),
  width: 300,
  flex: 1,
  minWidth: 200,
  sortable: true,
  filterable: true,
  ...options
});

export const createCenteredNumericColumn = <T extends GridValidRowModel>(
  field: keyof T,
  headerName: string,
  width: number = 150,
  options?: Partial<GridColDef<T>>
): GridColDef<T> => ({
  field: field as string,
  headerName,
  width,
  align: 'center',
  headerAlign: 'center',
  valueFormatter: value => String(value),
  sortable: true,
  filterable: true,
  ...options
});
